/**
 * This breadcrumb component supports generating breadcrumbs in two different ways: 
 * 1. From the standard drupal menu path, which is powered by the graphql query
 * 2. By rendering breadcrumbs from an array of links manually passed into the component.
 * 
 * As a result, this component is kind of complicated since it's handling two logical paths. 
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link, StaticQuery, graphql } from 'gatsby';
import { getURL, getAbsoluteLink} from '../../uglib_utils/utils'
import { globalHistory } from "@reach/router"
import RichResult from '../../uglib_components/richResult';

// Render a list of breadcrumb links to rich results markup
function linksToRichResults(links, title, current_page) {
	const url = getURL();

	let result = {
		"@context": "https://schema.org",
      	"@type": "BreadcrumbList",
		"itemListElement": [
			{
				"@type": "ListItem",
				"position": 1,
				"name": "Home",
				"item": url
			}
		]
	}

	const items = links.map((item, index) => {
		return {
			"@type": "ListItem",
			"position": index + 2,
			"name": item.title,
			"item": getAbsoluteLink(item.url)
		}
	})

	result.itemListElement.push(...items);

	result.itemListElement.push({
		"@type": "ListItem",
		"position": items.length + 2,
		"name": title,
		"item": getAbsoluteLink(current_page)
	})

	return result;
}

// Render a list of breadcrumbs to rich result markup.
function crumbsToRichResults(crumbs, title, current_page) {
	const url = getURL();

	let result = {
		"@context": "https://schema.org",
      	"@type": "BreadcrumbList",
		"itemListElement": [
			{
				"@type": "ListItem",
				"position": 1,
				"name": "Home",
				"item": url
			}
		]
	}

	const items = crumbs.map((item, index) => {
		return {
			"@type": "ListItem",
			"position": index + 2,
			"name": item.node.title,
			"item": getAbsoluteLink(item.node.link.url)
		}
	})

	result.itemListElement.push(...items);

	result.itemListElement.push({
		"@type": "ListItem",
		"position": items.length + 2,
		"name": title,
		"item": getAbsoluteLink(current_page)
	})

	return result;
}

/***
* Recursive function to iterate through the menu in search of parents
* Keeps going until it finds the top item with null parent
***/
function findCrumbs(menu, nextCrumb) {

    let crumbArray, result = [];
    let parentID;

    menu.forEach(item => {
        if (item.node.drupal_id === nextCrumb) {
            result.push(item);          
            if (item.node.drupal_parent_menu_item != null) {
                parentID = stripParentID(item.node.drupal_parent_menu_item);
                result = result.concat(findCrumbs(menu, parentID));
            }
        }
    })
    crumbArray = result.reverse();
    return crumbArray;
}
    
function stripParentID(drupalParentID) {
    if (drupalParentID) {
        return drupalParentID.replace("menu_link_content:", '');
    }
}

const makeBreadcrumbTrail = (menuData, domains, menus, links, nodeID, nodeTitle) => {

    let pageMenu = [];
    let midCrumbs = [];
    let rootItems = [];
    let homeCrumb = "Home";
    let homeCrumbURL = "/";
    let topCrumb;
    let topCrumbID;
    let topCrumbURL;
    let endCrumb;
    let endCrumbParent;
    const currentPage = `entity:node/` + String(nodeID);
    const pageTitle = nodeTitle;
	
	// First, handle instances where we pass in a list of breadcrumb links to the component. 
	if(links.length > 0) {
		
		const rich_results = linksToRichResults(links, pageTitle, globalHistory.location.pathname);
		const breadcrumbs = links.map(menuLink => {
			if(menuLink.url === "") {
				return (
					<li key={menuLink.id} className="breadcrumb-item">
						<span>{menuLink.title}</span>
					</li>
				);
			}
			else {
				return (
					<li key={menuLink.id} className="breadcrumb-item">
						{ <Link to={menuLink.url}>{menuLink.title}</Link> }
					</li>
				);
			}	
		});

		return (<>
			<RichResult data={rich_results} />
			<div className="breadcrumbs loaded">
				<div className="container">
					<div className="row">
						<div className="col-sm-12">
							<div className="site-breadcrumbs">			
							<ol className="breadcrumb breadcrumb-right-tag">								
								<li key={homeCrumbURL + `home`} className="breadcrumb-item topcrumb">									
									<a href="/">
										<i className='fa fa-home'></i>
                                        <span className='sr-only'>Home</span>
									</a>
								</li>
								{breadcrumbs}
								<li key={endCrumb + `end`} className="breadcrumb-item">{pageTitle}</li>
							</ol>							
							</div>
						</div>
					</div>
				</div>
			</div>			
		</>)	
	}

	// Fall back to using the page query if there were no links passed in.
    if (currentPage) {
        let allMenuItems = menuData.allMenuLinkContentMenuLinkContent.edges;

        if (allMenuItems) {
			// We only want certain menus to be used for breadcrumb trails
            allMenuItems.forEach(item => {
                if (menus.includes(item.node.menu_name)) {
                    pageMenu.push(item);
                }
            });
            
            if (pageMenu) {            
                pageMenu.forEach(item => {
                    if (!item.node.drupal_parent_menu_item) {
                        rootItems.push(item);
                    }
                });
                pageMenu.forEach(item => {
                    if (item.node.link.uri === currentPage) {
                        endCrumb = item.node.title;
                        endCrumbParent = stripParentID(item.node.drupal_parent_menu_item);
                    }
                });                
                midCrumbs = findCrumbs(pageMenu, endCrumbParent);
                
            }            
        }

		const rich_results = crumbsToRichResults(midCrumbs, pageTitle, globalHistory.location.pathname);

        return (<>
			<RichResult data={rich_results} />
            <div className="breadcrumbs loaded">
                <div className="container">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="site-breadcrumbs">          
                            <ol className="breadcrumb breadcrumb-right-tag">                                
                                <li key={homeCrumbURL + `home`} className="breadcrumb-item">                                    
                                    <a href={homeCrumbURL}>
                                        <i aria-hidden="true" className="fa-sharp fa-light fa-home"></i>
                                        <span className="visually-hidden">{homeCrumb}</span>
                                    </a>
                                </li>
                                {topCrumbURL && topCrumbID !== currentPage && 
                                    <li key={topCrumbURL} className="breadcrumb-item"><Link to={topCrumbURL}>{topCrumb}</Link></li>}
                                {midCrumbs && midCrumbs.map(midCrumb => {
                                    return <><li key={midCrumb.node.link.url + `mid`} className="breadcrumb-item">
                                            <Link to={midCrumb.node.link.url}>{midCrumb.node.title}</Link>
                                    </li></>
                                })}
                                <li key={endCrumb + `end`} className="breadcrumb-item">{endCrumb ? endCrumb : pageTitle}</li>                           
                            </ol>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>)        
    }
	

    return null;
}

const Breadcrumbs = (props) => (
   <StaticQuery
      query={
        graphql`query BreadcrumbMenuQuery {
                    allMenuLinkContentMenuLinkContent(
                        sort: {weight: ASC}
                        filter: {enabled: {eq: true}}
                    ) {
                        edges {
                        node {
                            drupal_id
                            drupal_parent_menu_item
                            menu_name
                            link {
                            uri
                            url
                            }
                            title
                            weight
                        }
                        }
                    }
                }`
      }
      render={data => makeBreadcrumbTrail(data, props.domains, props.menus, props.links, props.nodeID, props.nodeTitle)}
   />
)

Breadcrumbs.propTypes = {
    domains: PropTypes.array,
    menus: PropTypes.array,
    nodeID: PropTypes.number,
    nodeTitle: PropTypes.string,
	links: PropTypes.array,
}

Breadcrumbs.defaultProps = {
    domains: [],
    menus: ['main', 'footer'],
    nodeID: null,
    nodeTitle: ``,
	links: [],
}

export default Breadcrumbs